import { useState, useMemo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Scale, Minus, Plus, Divide,
  Landmark, Receipt, RefreshCw, Globe,
  ArrowRightLeft, Users, UserPlus,
  Brush, Calculator, Banknote, PieChart, ArrowLeft,
} from 'lucide-react';

// --- CONSTANTS ---
const DEFAULT_SMI_COST_30H = 1350;

// --- LOCAL COMPONENTS ---
const Card = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div className={`bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden ${className}`}>
    {children}
  </div>
);

const SectionTitle = ({
  icon: Icon,
  title,
  colorClass = 'text-emerald-800',
}: {
  icon: React.ElementType;
  title: string;
  colorClass?: string;
}) => (
  <div className={`flex items-center gap-2 mb-4 font-semibold border-b pb-2 border-gray-100 ${colorClass}`}>
    <Icon size={20} />
    <span>{title}</span>
  </div>
);

const InputField = ({
  label,
  value,
  onChange,
  suffix = '',
  step = '1',
  min = '0',
  className = '',
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  step?: string;
  min?: string;
  className?: string;
}) => (
  <div className={`mb-4 ${className}`}>
    <label className="block text-sm font-medium text-gray-600 mb-1 truncate" title={label}>
      {label}
    </label>
    <div className="relative group">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        step={step}
        min={min}
        className="w-full p-2 pr-8 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-right font-mono text-lg text-slate-800"
      />
      <span className="absolute right-3 top-2.5 text-gray-400 text-sm font-medium">{suffix}</span>
    </div>
  </div>
);

const RangeSlider = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  color = 'emerald',
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  color?: 'emerald' | 'blue' | 'purple';
}) => {
  const colorMap = {
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100 accent-emerald-600',
    blue: 'text-blue-600 bg-blue-50 border-blue-100 accent-blue-600',
    purple: 'text-purple-600 bg-purple-50 border-purple-100 accent-purple-600',
  };
  const activeColor = colorMap[color];
  const [textColor, bgColor, borderColor, accentColor] = activeColor.split(' ');

  return (
    <div className="mb-6">
      <div className="flex justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className={`text-sm font-bold px-2 py-0.5 rounded border ${textColor} ${bgColor} ${borderColor}`}>
          {value}%
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer ${accentColor}`}
      />
    </div>
  );
};

const ResultRow = ({
  label,
  value,
  subtext,
  isTotal = false,
  isNegative = false,
  highlight = false,
  colorClass = '',
  percentage = null,
}: {
  label: string;
  value: number;
  subtext?: string;
  isTotal?: boolean;
  isNegative?: boolean;
  highlight?: boolean;
  colorClass?: string;
  percentage?: number | null;
}) => (
  <div
    className={`flex justify-between items-center py-2 ${
      isTotal ? 'border-t-2 border-gray-200 mt-2 pt-2' : 'border-b border-gray-50 last:border-0'
    }`}
  >
    <div className="flex flex-col">
      <span
        className={`${isTotal ? 'font-bold text-gray-900' : 'text-gray-600'} ${
          highlight ? 'text-emerald-700 font-semibold' : ''
        } ${colorClass}`}
      >
        {label}
      </span>
      {subtext && <span className="text-xs text-gray-400">{subtext}</span>}
    </div>
    <div className="text-right">
      <span
        className={`font-mono block ${isTotal ? 'text-lg font-bold' : ''} ${
          isNegative ? 'text-red-500' : 'text-gray-800'
        }`}
      >
        {value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
      </span>
      {percentage !== null && (
        <span className="text-[10px] font-bold text-gray-400">{percentage.toFixed(1)}% del Total</span>
      )}
    </div>
  </div>
);

const CalcRow = ({
  label,
  value,
  operation = null,
  isTotal = false,
  note = '',
  isBold = false,
}: {
  label: string;
  value: number;
  operation?: 'plus' | 'minus' | 'divide' | 'equals' | null;
  isTotal?: boolean;
  note?: string;
  isBold?: boolean;
}) => (
  <div
    className={`flex justify-between items-center py-1 text-xs ${
      isTotal ? 'font-bold border-t border-gray-300 mt-1 pt-1 text-sm' : 'text-gray-600'
    }`}
  >
    <div className="flex items-center gap-2">
      {operation === 'plus' && <Plus size={10} className="text-gray-400" />}
      {operation === 'minus' && <Minus size={10} className="text-red-400" />}
      {operation === 'divide' && <Divide size={10} className="text-gray-400" />}
      {operation === 'equals' && <span className="text-gray-400">=</span>}
      <span className={isBold ? 'font-bold text-gray-800' : ''}>{label}</span>
    </div>
    <div className="text-right">
      <span
        className={`${operation === 'minus' ? 'text-red-500' : 'text-gray-800'} ${
          isTotal ? 'text-gray-900' : ''
        } ${isBold ? 'font-bold' : ''}`}
      >
        {operation === 'minus' && '-'}
        {value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
      </span>
      {note && <div className="text-[9px] text-gray-400">{note}</div>}
    </div>
  </div>
);

// --- MAIN COMPONENT ---
export default function CalculadoraGestionPage() {
  const [totalRevenue, setTotalRevenue] = useState(190000);
  const [numReservas, setNumReservas] = useState(162);
  const [cleaningUnitPrice, setCleaningUnitPrice] = useState(75);
  const [rentingUnitPrice, setRentingUnitPrice] = useState(0);
  const [workerMonthlyCost, setWorkerMonthlyCost] = useState(DEFAULT_SMI_COST_30H);
  const [otaType, setOtaType] = useState('AIRBNB');
  const [otaComm, setOtaComm] = useState(15);
  const [mgmtComm, setMgmtComm] = useState(20);
  const [ivaRate, setIvaRate] = useState(21);
  const [ownerSplit, setOwnerSplit] = useState(80);
  const [ivaTuristico] = useState(10);
  const [irpfRetencion, setIrpfRetencion] = useState(19);

  const handleOtaChange = (type: string) => {
    setOtaType(type);
    if (type === 'DIRECTA') setOtaComm(5);
    if (type === 'AIRBNB') setOtaComm(15);
    if (type === 'BOOKING') setOtaComm(16.1);
  };

  const isDirect = otaType === 'DIRECTA';

  const results = useMemo(() => {
    const totalPvp = totalRevenue;
    const totalCleaningGuest = cleaningUnitPrice * numReservas;
    const totalRenting = rentingUnitPrice * numReservas;
    const totalServiceRevenueGross = totalCleaningGuest + totalRenting;
    const serviceRevenueBase = totalServiceRevenueGross / (1 + ivaRate / 100);
    const serviceRevenueIVA = totalServiceRevenueGross - serviceRevenueBase;
    const hiringBudget = serviceRevenueBase;
    const potentialHires = hiringBudget / workerMonthlyCost;
    const totalOtaCost = totalPvp * (otaComm / 100);

    // Modelo Gestión
    const ownerNetBaseGest = totalPvp - totalCleaningGuest - totalOtaCost;
    const mgmtFee = ownerNetBaseGest * (mgmtComm / 100);
    const mgmtFeeIva = mgmtFee * (ivaRate / 100);
    const mgmtFeeTotal = mgmtFee + mgmtFeeIva;
    let managerNetGest = mgmtFee;
    if (isDirect) managerNetGest += totalOtaCost;
    const otaIva = totalOtaCost * (ivaRate / 100);
    const ownerNetFinalPayout = ownerNetBaseGest - mgmtFeeTotal - otaIva - totalRenting;

    // Modelo Explotación
    const precioBruto = totalPvp / (1 + ivaTuristico / 100);
    const explotIvaRepercutido = totalPvp - precioBruto;
    const precioNeto = precioBruto - totalServiceRevenueGross - totalOtaCost;
    const ownerBaseExplot = precioNeto * (ownerSplit / 100);
    const explotOwnerIva = ownerBaseExplot * (ivaRate / 100);
    const explotOwnerIrpf = ownerBaseExplot * (irpfRetencion / 100);
    const explotOwnerTotal = ownerBaseExplot + explotOwnerIva - explotOwnerIrpf;
    const managerSplitShare = precioNeto - ownerBaseExplot;
    let managerNetExplot = managerSplitShare;
    if (isDirect) managerNetExplot += totalOtaCost;
    const explotIvaBalance = explotIvaRepercutido - explotOwnerIva;

    const ownerGestPercent = (ownerNetFinalPayout / totalPvp) * 100;
    const ownerExplotPercent = (ownerBaseExplot / totalPvp) * 100;

    let equilibriumOwnerSplit = 0;
    if (precioNeto > 0) {
      const targetManagerShareMoney = managerNetGest - (isDirect ? totalOtaCost : 0);
      const targetManagerSharePercent = targetManagerShareMoney / precioNeto;
      equilibriumOwnerSplit = (1 - targetManagerSharePercent) * 100;
    }

    return {
      totalPvp,
      totalServiceRevenueGross,
      serviceRevenueBase,
      serviceRevenueIVA,
      hiringBudget,
      potentialHires,
      ownerNetBaseGest,
      mgmtFee,
      managerNetGest,
      ownerNetFinalPayout,
      ownerGestPercent,
      ownerBaseExplot,
      managerSplitShare,
      managerNetExplot,
      explotOwnerTotal,
      explotIvaBalance,
      ownerExplotPercent,
      equilibriumOwnerSplit,
      mgmtFeeTotal,
      otaIva,
      explotOwnerIva,
      explotOwnerIrpf,
      totalCleaningGuest,
      totalRenting,
      totalOtaCost,
      isDirect,
    };
  }, [
    totalRevenue, cleaningUnitPrice, rentingUnitPrice, workerMonthlyCost, numReservas,
    otaType, otaComm, mgmtComm, ivaRate, ownerSplit, ivaTuristico, irpfRetencion, isDirect,
  ]);

  const applyEquilibrium = () => {
    if (results.equilibriumOwnerSplit > 0) {
      setOwnerSplit(parseFloat(results.equilibriumOwnerSplit.toFixed(2)));
    }
  };

  const fmt = (v: number) =>
    v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/calculadoras"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sereno-dark transition-colors"
      >
        <ArrowLeft size={16} />
        Calculadoras
      </Link>

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-emerald-600 rounded-xl text-white">
          <Calculator size={28} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Calculadora Gestión & Explotación</h1>
          <p className="text-slate-500">Rentabilidad comparada y estrategia de contratación</p>
        </div>
      </div>

      {/* Hero: Calibración */}
      <div className="bg-slate-800 rounded-xl p-6 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-500/20 p-4 rounded-full border border-emerald-500/30">
              <Scale className="text-emerald-400" size={32} />
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase font-bold tracking-widest mb-1">
                Calibración de Rentabilidad
              </p>
              <h3 className="text-lg font-bold text-white mb-1">
                Para ganar lo mismo que en Gestión ({mgmtComm}%), en Explotación deberías dar al propietario un:
              </h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-emerald-400">
                  {results.equilibriumOwnerSplit.toFixed(2)}%
                </span>
                <span className="text-sm text-slate-400">(Actual pactado: {ownerSplit}%)</span>
              </div>
              <p className="text-slate-300 text-xs mt-2">
                {results.equilibriumOwnerSplit > ownerSplit
                  ? '🚀 Tu gestión actual es MUY rentable. Podrías pagarle más en explotación y seguirías ganando igual.'
                  : '⚠️ Tu gestión actual rinde menos que tu explotación estándar.'}
              </p>
            </div>
          </div>
          <div className="text-right hidden md:block">
            <button
              onClick={applyEquilibrium}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw size={14} /> Aplicar Equilibrio
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: INPUTS */}
        <div className="lg:col-span-4 space-y-6">
          {/* 1. Facturación */}
          <Card className="p-5 border-l-4 border-l-emerald-500">
            <SectionTitle icon={Globe} title="1. Datos de Facturación" colorClass="text-emerald-700" />
            <div className="space-y-4">
              <InputField label="Facturación Total (PVP)" value={totalRevenue} onChange={setTotalRevenue} suffix="€" />
              <InputField label="Número de Reservas (Periodo)" value={numReservas} onChange={setNumReservas} suffix="ud" />

              <div className="pl-4 border-l-2 border-emerald-100 space-y-4 pt-2">
                <p className="text-xs font-bold text-emerald-600 uppercase mb-0">Precios de Venta (PVP)</p>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Banknote size={14} className="text-blue-600" />
                    <span className="text-[11px] font-bold text-blue-800 uppercase">Por Reserva (Ingreso)</span>
                  </div>
                  <InputField className="mb-2" label="Precio Limpieza" value={cleaningUnitPrice} onChange={setCleaningUnitPrice} suffix="€" />
                  <InputField className="mb-0" label="Precio Extras/Renting" value={rentingUnitPrice} onChange={setRentingUnitPrice} suffix="€" />
                </div>
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus size={14} className="text-amber-600" />
                    <span className="text-[11px] font-bold text-amber-800 uppercase">Coste Laboral</span>
                  </div>
                  <InputField className="mb-0" label="Coste Empresa (Nómina 30h)" value={workerMonthlyCost} onChange={setWorkerMonthlyCost} suffix="€" />
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Canal (OTA)</label>
              <div className="grid grid-cols-3 gap-1 mb-3">
                <button
                  onClick={() => handleOtaChange('DIRECTA')}
                  className={`py-2 px-1 text-[10px] font-bold rounded-l-lg border transition-colors ${otaType === 'DIRECTA' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  DIRECTA 5%
                </button>
                <button
                  onClick={() => handleOtaChange('AIRBNB')}
                  className={`py-2 px-1 text-[10px] font-bold border-y transition-colors ${otaType === 'AIRBNB' ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  AIRBNB 15%
                </button>
                <button
                  onClick={() => handleOtaChange('BOOKING')}
                  className={`py-2 px-1 text-[10px] font-bold rounded-r-lg border transition-colors ${otaType === 'BOOKING' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  BOOK 16%
                </button>
              </div>
              <InputField label="Comisión OTA (%)" value={otaComm} onChange={setOtaComm} suffix="%" />
            </div>
          </Card>

          {/* 2. El Trato */}
          <Card className="p-5 border-l-4 border-l-slate-500">
            <SectionTitle icon={Scale} title="2. El Trato (Parámetros)" colorClass="text-slate-700" />

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Gestión (Viviendas)</p>
                <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                  Actual: {mgmtComm}%
                </span>
              </div>
              <RangeSlider label="Comisión" value={mgmtComm} onChange={setMgmtComm} min={0} max={50} step={0.1} color="blue" />
            </div>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Explotación (AT)</p>
                <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                  Estándar: {ownerSplit}/{100 - ownerSplit}
                </span>
              </div>
              <RangeSlider label="Reparto Propietario" value={ownerSplit} onChange={setOwnerSplit} min={50} max={90} step={1} color="purple" />
            </div>

            <div className="pt-2 border-t border-gray-100 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <InputField label="IVA General" value={ivaRate} onChange={setIvaRate} suffix="%" />
                <InputField label="Retención IRPF" value={irpfRetencion} onChange={setIrpfRetencion} suffix="%" />
              </div>
            </div>

            <button
              onClick={applyEquilibrium}
              className="w-full mt-2 py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2 border border-slate-200"
            >
              <RefreshCw size={14} />
              <span>Igualar Explotación a {results.equilibriumOwnerSplit.toFixed(2)}%</span>
            </button>
          </Card>
        </div>

        {/* RIGHT: RESULTS */}
        <div className="lg:col-span-8 space-y-6">
          {/* Comparison Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gestión */}
            <Card className="border-t-4 border-t-blue-500 bg-gradient-to-b from-white to-blue-50/30">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                      Modelo Gestión
                      <span className="text-xs font-normal px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                        {mgmtComm}%
                      </span>
                    </h2>
                    <span className="text-[10px] text-gray-400">Típico en Viviendas</span>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-xl text-center min-w-[120px] border border-blue-100">
                    <p className="text-xs text-blue-500 uppercase font-bold tracking-wider">Tu Beneficio</p>
                    <p className="text-2xl font-bold text-blue-700">{fmt(results.managerNetGest)}</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm bg-white/50 p-3 rounded-lg border border-blue-100">
                  <p className="text-[10px] text-blue-400 uppercase font-bold mb-1">Tu Bolsillo de Gestor</p>
                  <ResultRow label="Comisión Base" value={results.mgmtFee} colorClass="font-bold text-gray-700" />
                  {isDirect && (
                    <ResultRow
                      label="Comisión Web (5%)"
                      value={results.totalOtaCost}
                      highlight
                      colorClass="text-emerald-600"
                      subtext="Ingreso Directo Extra"
                    />
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-blue-100 flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-gray-500 text-xs uppercase font-bold">Pago Propietario</span>
                    <span className="text-[10px] font-bold text-blue-600">
                      {results.ownerGestPercent.toFixed(1)}% del Total
                    </span>
                  </div>
                  <span className="font-bold text-gray-800 text-lg">{fmt(results.ownerNetFinalPayout)}</span>
                </div>
              </div>
            </Card>

            {/* Explotación */}
            <Card className="border-t-4 border-t-purple-500 bg-gradient-to-b from-white to-purple-50/30">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                      Modelo Explotación
                      <span className="text-xs font-normal px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                        {100 - ownerSplit}/{ownerSplit}
                      </span>
                    </h2>
                    <span className="text-[10px] text-gray-400">Típico en AT</span>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-xl text-center min-w-[120px] border border-purple-100">
                    <p className="text-xs text-purple-500 uppercase font-bold tracking-wider">Tu Beneficio</p>
                    <p className="text-2xl font-bold text-purple-700">{fmt(results.managerNetExplot)}</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm bg-white/50 p-3 rounded-lg border border-purple-100">
                  <p className="text-[10px] text-purple-400 uppercase font-bold mb-1">Tu Bolsillo de Gestor</p>
                  <ResultRow label="Reparto Neto" value={results.managerSplitShare} colorClass="font-bold text-gray-700" />
                  {isDirect && (
                    <ResultRow
                      label="Comisión Web (5%)"
                      value={results.totalOtaCost}
                      highlight
                      colorClass="text-emerald-600"
                      subtext="Recuperado (Ingreso Extra)"
                    />
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-purple-100 flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-gray-500 text-xs uppercase font-bold">Pago Propietario</span>
                    <span className="text-[10px] font-bold text-purple-600">
                      {results.ownerExplotPercent.toFixed(1)}% del Total
                    </span>
                  </div>
                  <span className="font-bold text-gray-700 text-lg">{fmt(results.ownerBaseExplot)}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Sereno Clean & Contratación */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tesorería Sereno Clean */}
            <div className="bg-slate-800 rounded-xl p-6 text-white shadow-xl relative overflow-hidden">
              <div className="relative z-10 flex flex-col justify-between h-full">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-amber-500/20 p-3 rounded-full border border-amber-500/30">
                    <Brush className="text-amber-400" size={28} />
                  </div>
                  <div>
                    <p className="text-amber-400 text-xs uppercase font-bold tracking-widest mb-1">
                      Tesorería Sereno Clean
                    </p>
                    <h3 className="text-xl font-bold text-white">¿Cuánto dinero REAL tengo?</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 border-t border-slate-700 pt-4">
                  <div>
                    <p className="text-xs text-slate-400 uppercase mb-1">Total Facturado (PVP)</p>
                    <p className="text-xl font-bold text-white">{fmt(results.totalServiceRevenueGross)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-red-400 uppercase mb-1">IVA (Para Hacienda)</p>
                    <p className="text-xl font-bold text-red-400">{fmt(results.serviceRevenueIVA)}</p>
                  </div>
                  <div className="col-span-2 bg-emerald-900/30 -m-2 p-3 rounded border border-emerald-500/30 mt-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-emerald-400 uppercase mb-1 font-bold">Base Imponible (Mío)</p>
                        <p className="text-2xl font-bold text-emerald-400">{fmt(results.serviceRevenueBase)}</p>
                      </div>
                      <PieChart className="text-emerald-500/50" size={32} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Capacidad de Contratación */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-l-emerald-500 shadow-lg flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-4 text-emerald-700 font-bold text-sm uppercase tracking-wide border-b border-gray-100 pb-2">
                <Users size={18} />
                <span>Poder de Contratación</span>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase mb-1">Con tu Base Imponible puedes pagar:</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-emerald-600">
                      {results.potentialHires.toFixed(1)}
                    </span>
                    <span className="text-sm font-medium text-gray-700">Nóminas (30h)</span>
                  </div>
                </div>
                <div className="text-xs bg-gray-50 p-3 rounded text-gray-500 border border-gray-200">
                  <div className="flex justify-between mb-1">
                    <span>Coste Unitario Nómina:</span>
                    <strong>{workerMonthlyCost.toLocaleString()}€</strong>
                  </div>
                  <p className="text-[10px] italic text-right opacity-70">Cálculo: Base Imponible / Coste Nómina</p>
                </div>
              </div>
            </div>
          </div>

          {/* Liquidaciones */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-bold text-gray-500 mb-4 flex items-center gap-2 uppercase tracking-wider">
              <Landmark size={16} /> Liquidaciones y Tesorería
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Gestión */}
              <Card className="p-5 text-sm">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <Receipt className="text-blue-500" size={16} />
                  <h4 className="font-bold text-gray-700">Gestión: Flujo de Caja</h4>
                </div>
                <div className="space-y-1">
                  <CalcRow label="Total Recaudado (PVP)" value={results.totalPvp} isBold />
                  <CalcRow label="(-) A Sereno Clean (Bruto)" value={results.totalServiceRevenueGross} operation="minus" note="Se deriva íntegro" />
                  <CalcRow label={`(-) OTA ${isDirect ? '(Sereno)' : ''}`} value={results.totalOtaCost} operation="minus" />
                  <div className="my-1 border-t border-dashed border-gray-200" />
                  <CalcRow label="NETO BASE PROPIETARIO" value={results.ownerNetBaseGest} isBold />
                  <CalcRow label="(-) Tu Comisión (+IVA)" value={results.mgmtFeeTotal} operation="minus" />
                  <CalcRow label={`(-) IVA ${isDirect ? 'Web Sereno' : 'OTA Intracom'}`} value={results.otaIva} operation="minus" />
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <CalcRow label="PAGO FINAL PROPIETARIO" value={results.ownerNetFinalPayout} isTotal />
                  </div>
                </div>
              </Card>

              {/* Explotación */}
              <Card className="p-5 text-sm">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <ArrowRightLeft className="text-purple-500" size={16} />
                  <h4 className="font-bold text-gray-700">Explotación: Flujo de Caja</h4>
                </div>
                <div className="space-y-1">
                  <CalcRow label="Bruto Recaudado" value={results.totalPvp / (1 + ivaTuristico / 100)} isBold />
                  <CalcRow label="(-) A Sereno Clean (Bruto)" value={results.totalServiceRevenueGross} operation="minus" note="Coste Operativo Deducido" />
                  <CalcRow label="(-) OTA" value={results.totalOtaCost} operation="minus" />
                  <div className="my-1 border-t border-dashed border-gray-200" />
                  <CalcRow label="NETO A REPARTIR" value={results.ownerBaseExplot + results.managerSplitShare} isBold />
                  <CalcRow label="Parte Propietario" value={results.ownerBaseExplot} />
                  <CalcRow label="(+) IVA / (-) IRPF" value={results.explotOwnerIva - results.explotOwnerIrpf} />
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <CalcRow label="TRANSFERENCIA PROPIETARIO" value={results.explotOwnerTotal} isTotal />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
